import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Paper, 
  Divider, 
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { PhysiologicalRiskEvent, PhysiologicalStatus } from '../types/wearable';
import PhysiologicalAlertList from '../components/wearable/PhysiologicalAlertList';
import PhysiologicalMonitor from '../components/wearable/PhysiologicalMonitor';

// 임시 데이터 및 API 연동 (실제 환경에서는 API로 대체)
const API_URL = '/api';

const PhysiologicalMonitoringPage: React.FC = () => {
  const [activeMembers, setActiveMembers] = useState<any[]>([]);
  const [riskEvents, setRiskEvents] = useState<PhysiologicalRiskEvent[]>([]);
  const [physiologicalData, setPhysiologicalData] = useState<Record<string, PhysiologicalStatus>>({});
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // 위험 이벤트 데이터 가져오기
  const fetchRiskEvents = useCallback(async () => {
    setLoadingEvents(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/events?type=physiological_risk`);
      if (!response.ok) {
        throw new Error('위험 이벤트를 가져오는 중 오류 발생');
      }
      
      const data = await response.json();
      setRiskEvents(data);
    } catch (err) {
      console.error('이벤트 데이터 가져오기 오류:', err);
      setError('위험 이벤트를 가져오는 중 오류 발생');
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  // 활성 멤버 데이터 가져오기
  const fetchActiveMembers = useCallback(async () => {
    setLoadingMembers(true);
    
    try {
      const response = await fetch(`${API_URL}/wearables/active-members`);
      if (!response.ok) {
        throw new Error('활성 멤버 데이터를 가져오는 중 오류 발생');
      }
      
      const data = await response.json();
      setActiveMembers(data);
      
      // 각 멤버의 생체 데이터 가져오기
      await Promise.all(data.map(async (member: any) => {
        if (member.wearable_id) {
          await fetchPhysiologicalData(member.wearable_id);
        }
      }));
    } catch (err) {
      console.error('활성 멤버 데이터 가져오기 오류:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  // 생체 데이터 가져오기
  const fetchPhysiologicalData = async (wearableId: string) => {
    try {
      const response = await fetch(`${API_URL}/wearables/${wearableId}/physiological-status`);
      if (!response.ok) {
        return;
      }
      
      const data = await response.json();
      setPhysiologicalData(prev => ({
        ...prev,
        [wearableId]: data
      }));
    } catch (err) {
      console.error(`웨어러블 ${wearableId} 생체 데이터 가져오기 오류:`, err);
    }
  };

  // 이벤트 해결 처리
  const handleResolveEvent = async (eventId: string) => {
    try {
      const response = await fetch(`${API_URL}/events/${eventId}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'resolved' })
      });
      
      if (!response.ok) {
        throw new Error('이벤트 해결 처리 중 오류 발생');
      }
      
      // 이벤트 상태 업데이트
      setRiskEvents(prev => 
        prev.map(event => 
          event.id === eventId ? { ...event, status: 'resolved' } : event
        )
      );
      
      // 성공 메시지 표시
      setSnackbar({
        open: true,
        message: '이벤트가 해결 처리됨',
        severity: 'success'
      });
    } catch (err) {
      console.error('이벤트 해결 처리 오류:', err);
      setSnackbar({
        open: true,
        message: '이벤트 해결 처리 중 오류 발생',
        severity: 'error'
      });
    }
  };

  // 스낵바 닫기
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 페이지 로드 시 데이터 가져오기
  useEffect(() => {
    fetchRiskEvents();
    fetchActiveMembers();
    
    // 10초마다 데이터 갱신
    const interval = setInterval(() => {
      fetchRiskEvents();
      fetchActiveMembers();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchRiskEvents, fetchActiveMembers]);

  // 활성 회원 중 위험 상태인 회원만 필터링
  const membersAtRisk = activeMembers.filter(member => {
    const status = physiologicalData[member.wearable_id];
    return status && status.available && (status.risk_status === '위험' || status.risk_status === 'risk');
  });

  // 활성 회원 중 안전한 회원만 필터링
  const safeMembers = activeMembers.filter(member => {
    const status = physiologicalData[member.wearable_id];
    return status && status.available && (status.risk_status === '안전' || status.risk_status === 'safe');
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        생체 신호 모니터링 및 위험 관리
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        {/* 위험 이벤트 리스트 */}
        <Grid item xs={12} md={4}>
          <PhysiologicalAlertList 
            events={riskEvents}
            loading={loadingEvents}
            error={error || undefined}
            onResolve={handleResolveEvent}
            onRefresh={fetchRiskEvents}
          />
        </Grid>
        
        {/* 생체 모니터링 영역 */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              위험 상태 회원 모니터링 
              <Typography component="span" variant="body2" color="error" sx={{ ml: 1 }}>
                ({membersAtRisk.length}명)
              </Typography>
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            {loadingMembers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : membersAtRisk.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                현재 위험 상태인 회원 없음
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {membersAtRisk.map(member => (
                  <Grid item xs={12} md={6} key={member.id}>
                    <PhysiologicalMonitor 
                      wearableId={member.wearable_id}
                      physiologicalStatus={physiologicalData[member.wearable_id]}
                      memberName={member.name}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
          
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              안전 상태 회원 모니터링
              <Typography component="span" variant="body2" color="success" sx={{ ml: 1 }}>
                ({safeMembers.length}명)
              </Typography>
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            {loadingMembers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : safeMembers.length === 0 ? (
              <Alert severity="info">
                현재 안전 상태 회원 없음
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {safeMembers.map(member => (
                  <Grid item xs={12} md={6} lg={4} key={member.id}>
                    <PhysiologicalMonitor 
                      wearableId={member.wearable_id}
                      physiologicalStatus={physiologicalData[member.wearable_id]}
                      memberName={member.name}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* 알림 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PhysiologicalMonitoringPage; 